import * as React from "react";

import { moment } from "../../ExternalHacks";

import {
  CalendarInput,
  Loading,
  MultiLineTextInput,
  ProminentErrorMessageBox,
  SingleLineTextInput,
  ValueSelectGridInput,
} from "../../Common";

import {
  GiftcardAmount,
  PurchaseFormLogic,
  useGiftcardPurchase,
} from "../providers";

import { BagModal } from "../bag-modal";

import { About } from "./about";

export const PurchasePage: React.FunctionComponent = () => {
  const {
    isAddingToBag,

    possibleGiftcardAmounts,

    purchaseForm,

    onFormFieldFocus,
    onFormInputChange,
    onFormInputBlur,

    onAddToBag,
    scrollToError,
    onHasScrolledToError,
    reset,
  } = useGiftcardPurchase();

  const scrollToJustAbove = (element: Element, margin: number = 20) => {
    const elementRect = element.getBoundingClientRect().top;
    const elementPosition = elementRect + window.pageYOffset;
    const offsetPosition = elementPosition - margin;

    window.scrollTo({ top: offsetPosition, behavior: "smooth" });
  };

  React.useEffect(() => {
    if (scrollToError) {
      let hasScrolled = false;
      Object.keys(PurchaseFormLogic).forEach((key) => {
        if (!hasScrolled) {
          const errorPropertyKey = `${key}Error`;
          const errorProperty = purchaseForm[errorPropertyKey];
          if (errorProperty) {
            const element = document.querySelector(
              `.content.purchase-page .named-${key}`
            );
            if (element) {
              const isMobile = window.matchMedia("(max-width: 767px)").matches;
              scrollToJustAbove(element, isMobile ? 170 : 145);

              hasScrolled = true;
            }
          }
        }
      });
      onHasScrolledToError();
    }
  }, [purchaseForm, scrollToError]);

  const onFormSubmit: React.MouseEventHandler<HTMLButtonElement> = (event) => {
    event.preventDefault();
    onAddToBag();
    reset();
  };

  const indexOfZero = possibleGiftcardAmounts.indexOf('0.0000');

  if (indexOfZero !== -1) {
    possibleGiftcardAmounts.splice(indexOfZero, 1);
  }

  return (
    <React.Fragment>
      <div className="content purchase-page">
        <form>
          <ValueSelectGridInput<GiftcardAmount>
            displayName="Choose the e-gift card amount."
            name="giftcardAmount"
            form={purchaseForm}
            possibleValues={possibleGiftcardAmounts}
            onChange={onFormInputChange}
            presentPossibleValueString={(value: any) => {
              const valueAsFloat = parseFloat(value);

              let valueAsInteger;
              if (!isNaN(valueAsFloat)) {
                valueAsInteger = valueAsFloat.toFixed(0);
              } else {
                valueAsInteger = 0;
              }

              const asIntegerDollarValueString = `$${valueAsInteger}`;
              return asIntegerDollarValueString;
            }}
            keyForPossibleValue={(value: GiftcardAmount) => value}
          />

          <h2>Add delivery details.</h2>
          <div className="delivery-details-form">
            <div className="name-inputs-container">
              <SingleLineTextInput
                required
                name="to"
                displayName="To"
                placeholder="Recipient’s name"
                onFocus={onFormFieldFocus}
                onChange={onFormInputChange}
                onBlur={onFormInputBlur}
                form={purchaseForm}
              />
              <SingleLineTextInput
                required
                name="from"
                displayName="From"
                placeholder="Your name"
                onFocus={onFormFieldFocus}
                onChange={onFormInputChange}
                onBlur={onFormInputBlur}
                form={purchaseForm}
              />
            </div>
            <SingleLineTextInput
              required
              name="emailAddress"
              displayName="Email Address"
              type="email"
              inputMode="email"
              autoComplete="email"
              notice="We can send the e-gift card to your email or directly to the recipient’s."
              onFocus={onFormFieldFocus}
              onChange={onFormInputChange}
              onBlur={onFormInputBlur}
              form={purchaseForm}
            />
            <CalendarInput
              required
              name="deliveryDate"
              displayName="Delivery Date"
              notice="Gift cards can be scheduled for delivery up to three months in advance."
              onChange={onFormInputChange}
              form={purchaseForm}
              minDetail="year"
              minDate={moment().startOf("day").toDate()}
              maxDate={moment().startOf("day").add(3, "months").toDate()}
              transformDateToCalendarDateValue={(dateFromForm) => {
                return moment(dateFromForm).toDate();
              }}
              presentDate={(dateFromForm) => {
                const presentationFormattedDate =
                  moment(dateFromForm).format("DD / MM / YYYY");

                let presentedDate = `${presentationFormattedDate}`;
                if (moment().isSame(moment(dateFromForm), "day")) {
                  presentedDate = `${presentationFormattedDate} (Today)`;
                }

                return presentedDate;
              }}
            />
            <MultiLineTextInput
              name="personalMessage"
              displayName="Personal Message"
              notice={purchaseForm.personalMessageNotice}
              noticeWarningColor={
                purchaseForm.personalMessageNoticeWarningColor
              }
              onChange={onFormInputChange}
              form={purchaseForm}
              rows={5}
            />

            <ProminentErrorMessageBox />
          </div>

          <button className="add-to-bag" onClick={onFormSubmit}>
            Add to Bag
          </button>
        </form>
        <About />
        {isAddingToBag && <Loading />}
        <BagModal />
      </div>
    </React.Fragment>
  );
};
